/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ShopperBasketsV2 } from '@/scapi';

/**
 * True when a single PLI carries `c_priceIsAvailable=false` — the cartridge-side
 * `PricingAvailabilityHelper` stamped it during the last `CalculateBasket` pass
 * because the shopper's active zone price books have no coverage for the SKU
 * (or the merchandise product no longer resolves).
 *
 * Strict `=== false` (with SCAPI `'false'` string coercion). Legitimate-$0
 * exempt PLIs (Extend warranty, mattress-takeback `*RETURN`, non-catalog /
 * option / bundled / bonus) are LEFT UNSET on purpose and MUST fall through as
 * "priced" so their $0 renders normally.
 */
export function isPliPriceUnavailable(item: unknown): boolean {
    if (!item || typeof item !== 'object') return false;
    const flag = (item as { c_priceIsAvailable?: unknown }).c_priceIsAvailable;
    return flag === false || flag === 'false';
}

/**
 * True when at least one merchandise line in the basket has
 * `c_priceIsAvailable=false`. Callers use this to swap Order-Summary
 * Subtotal / Estimated Total from `formatCurrency(...)` (which would render a
 * misleading `$0.00` for the unpriced portion — the cartridge writes literal
 * `0` on unpriced PLIs to protect basket totals from `Money.NOT_AVAILABLE`
 * poisoning) to a "Price unavailable" label.
 *
 * Conservative "any PLI trips the whole summary" rule: matches the checkout
 * gate's reasoning (any unpriced PLI blocks the CTA), so shopper never sees a
 * partial subtotal alongside a disabled Proceed-to-Checkout button.
 */
export function basketHasPriceUnavailable(basket: unknown): boolean {
    if (!basket || typeof basket !== 'object') return false;
    const items = (basket as { productItems?: ShopperBasketsV2.schemas['ProductItem'][] }).productItems;
    if (!Array.isArray(items)) return false;
    for (const item of items) {
        if (isPliPriceUnavailable(item)) return true;
    }
    return false;
}
